import React from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Card, 
  CardContent, 
  Chip, 
  Grid,
  Button,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import { 
  ArrowBack, 
  Warning,
  CheckCircle,
  Cancel,
  Info,
  CalendarToday
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

interface Penalty {
  id: string;
  type: '경고' | '정지' | '제재';
  reason: string;
  date: string;
  status: '활성' | '해제' | '만료';
  description: string;
  campaignTitle: string;
}

const mockPenalties: Penalty[] = [
  {
    id: '1',
    type: '경고',
    reason: '리뷰 등록 지연',
    date: '2024-01-15',
    status: '활성',
    description: '캠페인 리뷰를 마감일 이후에 등록하여 경고를 받았습니다.',
    campaignTitle: '서울 강남구 맛집 탐방'
  },
  {
    id: '2',
    type: '정지',
    reason: '부적절한 리뷰 내용',
    date: '2024-01-10',
    status: '해제',
    description: '캠페인 리뷰에서 부적절한 내용을 포함하여 7일간 정지되었습니다.',
    campaignTitle: '부산 해운대 카페 투어'
  }
];

const getPenaltyColor = (type: string) => {
  switch (type) {
    case '경고': return 'warning';
    case '정지': return 'error';
    case '제재': return 'error';
    default: return 'default';
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case '활성': return 'error';
    case '해제': return 'success';
    case '만료': return 'default';
    default: return 'default';
  }
};

const ReviewerPenaltyPage: React.FC = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate('/mypage/reviewer');
  };

  const activePenalties = mockPenalties.filter(penalty => penalty.status === '활성');
  const resolvedPenalties = mockPenalties.filter(penalty => penalty.status !== '활성');

  return (
    <Container maxWidth="md" sx={{ pb: 8 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, mt: 2 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={handleBack}
          sx={{ mr: 2 }}
        >
          뒤로
        </Button>
        <Typography variant="h5" component="h1">
          페널티 관리
        </Typography>
      </Box>

      {/* Summary Alert */}
      {activePenalties.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2">
            현재 <strong>{activePenalties.length}개의 활성 페널티</strong>가 있습니다.
            페널티가 해제될 때까지 일부 기능이 제한될 수 있습니다.
          </Typography>
        </Alert>
      )}

      {/* Active Penalties */}
      {activePenalties.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning color="warning" />
            활성 페널티
          </Typography>
          <Grid container spacing={2}>
            {activePenalties.map((penalty) => (
              <Grid item xs={12} key={penalty.id}>
                <Card sx={{ border: '2px solid', borderColor: 'warning.main' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box>
                        <Typography variant="h6" component="h2" gutterBottom>
                          {penalty.campaignTitle}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {penalty.description}
                        </Typography>
                      </Box>
                      <Chip
                        label={penalty.type}
                        color={getPenaltyColor(penalty.type) as any}
                        size="small"
                      />
                    </Box>
                    
                    <Divider sx={{ my: 2 }} />
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarToday fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          {penalty.date}
                        </Typography>
                      </Box>
                      <Chip
                        label={penalty.status}
                        color={getStatusColor(penalty.status) as any}
                        size="small"
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Resolved Penalties */}
      {resolvedPenalties.length > 0 && (
        <Box>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircle color="success" />
            해제된 페널티
          </Typography>
          <Grid container spacing={2}>
            {resolvedPenalties.map((penalty) => (
              <Grid item xs={12} key={penalty.id}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box>
                        <Typography variant="h6" component="h2" gutterBottom>
                          {penalty.campaignTitle}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {penalty.description}
                        </Typography>
                      </Box>
                      <Chip
                        label={penalty.type}
                        color={getPenaltyColor(penalty.type) as any}
                        size="small"
                      />
                    </Box>
                    
                    <Divider sx={{ my: 2 }} />
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarToday fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          {penalty.date}
                        </Typography>
                      </Box>
                      <Chip
                        label={penalty.status}
                        color={getStatusColor(penalty.status) as any}
                        size="small"
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* No Penalties */}
      {mockPenalties.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            페널티 내역이 없습니다
          </Typography>
          <Typography variant="body2" color="text.secondary">
            캠페인 가이드라인을 잘 지켜주세요!
          </Typography>
        </Box>
      )}

      {/* Penalty Guidelines */}
      <Card sx={{ mt: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Info color="info" />
            페널티 가이드라인
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon>
                <Warning color="warning" />
              </ListItemIcon>
              <ListItemText 
                primary="리뷰 등록 지연"
                secondary="마감일 이후 리뷰 등록 시 경고 또는 정지"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Cancel color="error" />
              </ListItemIcon>
              <ListItemText 
                primary="부적절한 리뷰 내용"
                secondary="욕설, 비방, 허위 정보 포함 시 정지"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Cancel color="error" />
              </ListItemIcon>
              <ListItemText 
                primary="중복 신청"
                secondary="동일 캠페인 중복 신청 시 제재"
              />
            </ListItem>
          </List>
        </CardContent>
      </Card>
    </Container>
  );
};

export default ReviewerPenaltyPage; 